import React, { useState } from 'react';
import { useJobs } from '../../jobs/hooks/useJobs';
import { Calendar, Plus, Trash2, AlertCircle } from 'lucide-react';
import { format } from 'date-fns';

export default function BlockedDatesManager() {
  const { blockedDates, loading, addBlockedDate, removeBlockedDate } = useJobs();
  const [showAddForm, setShowAddForm] = useState(false);
  const [newDate, setNewDate] = useState('');
  const [newReason, setNewReason] = useState('');
  const [error, setError] = useState('');

  const handleAddDate = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!newDate) {
      setError('Please select a date');
      return;
    }

    try {
      await addBlockedDate(newDate, newReason);
      setNewDate('');
      setNewReason('');
      setShowAddForm(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to block date');
    }
  };

  const handleRemoveDate = async (dateId: string) => {
    try {
      await removeBlockedDate(dateId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove blocked date');
    }
  };

  const formatDisplayDate = (dateString: string) => {
    try {
      return format(new Date(dateString), 'EEEE, MMMM d, yyyy');
    } catch {
      return dateString;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-gray-500">Loading blocked dates...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-gray-900 flex items-center">
          <Calendar className="h-5 w-5 mr-2 text-indigo-600" />
          Blocked Dates Management
        </h2>
        <button
          onClick={() => setShowAddForm(true)}
          className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 flex items-center"
        >
          <Plus className="h-4 w-4 mr-2" />
          Block Date
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center">
          <AlertCircle className="h-5 w-5 text-red-600 mr-2" />
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {/* Add Date Form */}
      {showAddForm && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Block New Date</h3>
          <form onSubmit={handleAddDate} className="space-y-4">
            <div>
              <label htmlFor="date" className="block text-sm font-medium text-gray-700 mb-1">
                Date
              </label>
              <input
                type="date"
                id="date"
                value={newDate}
                onChange={(e) => setNewDate(e.target.value)}
                min={new Date().toISOString().split('T')[0]} // Prevent past dates
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                required
              />
            </div>
            <div>
              <label htmlFor="reason" className="block text-sm font-medium text-gray-700 mb-1">
                Reason (optional)
              </label>
              <input
                type="text"
                id="reason"
                value={newReason}
                onChange={(e) => setNewReason(e.target.value)}
                placeholder="e.g., Holiday, Maintenance, Fully Booked"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
            <div className="flex space-x-3">
              <button
                type="submit"
                className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700"
              >
                Block Date
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowAddForm(false);
                  setNewDate('');
                  setNewReason('');
                  setError('');
                }}
                className="bg-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-400"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Blocked Dates List */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">
            Blocked Dates ({blockedDates.length})
          </h3>
        </div>
        
        {blockedDates.length === 0 ? (
          <div className="p-6 text-center text-gray-500">
            <Calendar className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <p>No dates are currently blocked</p>
            <p className="text-sm">Block specific dates to prevent customer bookings</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {blockedDates.map((blockedDate) => (
              <div key={blockedDate.id} className="p-6 flex items-center justify-between">
                <div>
                  <p className="font-medium text-gray-900">
                    {formatDisplayDate(blockedDate.date)}
                  </p>
                  {blockedDate.reason && (
                    <p className="text-sm text-gray-600 mt-1">{blockedDate.reason}</p>
                  )}
                  <p className="text-xs text-gray-400 mt-1">
                    Blocked on {format(new Date(blockedDate.createdAt), 'MMM d, yyyy')}
                  </p>
                </div>
                <button
                  onClick={() => handleRemoveDate(blockedDate.id)}
                  className="text-red-600 hover:text-red-800 p-2 rounded-lg hover:bg-red-50"
                  title="Remove blocked date"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}